import { useEffect, useRef, useState } from "react";
import api, { apiError, fileUrl } from "@/lib/api";
import { fmtDate } from "@/lib/format";
import { COMPANY } from "@/lib/company";
import { useBranding, fetchBranding } from "@/lib/branding";
import { PageHeader, Empty, StatCard, Panel, Segmented, Cell } from "@/components/Shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Plus, Pencil, Trash2, Shield, HardHat, Bell, Clock, Send, Mail, Database, Download, RefreshCw, Loader2, RotateCcw, CheckCircle2, AlertTriangle, PackageSearch, ImageIcon, Upload, Users2, KeyRound, CalendarDays, ShieldCheck } from "lucide-react";
import QnbLiveCheck from "@/components/QnbLiveCheck";
import { TaxOfficeSelect } from "@/components/TaxOfficeSelect";
import PublishSyncCard from "@/components/PublishSyncCard";
import SecurityPanel from "@/components/security/SecurityPanel";
import { toast } from "sonner";
import { confirm } from "@/components/ConfirmDialog";

const fmtSize = (b) => {
  if (!b) return "0 B";
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / 1024 / 1024).toFixed(2)} MB`;
};
const fmtDateTime = (iso) => { try { return new Date(iso).toLocaleString("tr-TR", { day: "2-digit", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit" }); } catch { return iso; } };

const QNB_LABELS = { vkn: "VKN / TCKN", company_title: "Ünvan", company_address: "Adres", city: "İl", district: "İlçe", email: "E-posta", owner_name: "Mükellef Adı", tax_office: "Vergi Dairesi", username: "WS Kullanıcı", password: "WS Parola" };

const blank = { name: "", email: "", password: "", role: "worker", pin: "" };

export default function Users() {
  const [list, setList] = useState([]);
  const [dlg, setDlg] = useState(false);
  const [form, setForm] = useState(blank);
  const [editId, setEditId] = useState(null);
  const [notif, setNotif] = useState(null);
  const [extraStr, setExtraStr] = useState("");
  const [backups, setBackups] = useState([]);
  const [backupBusy, setBackupBusy] = useState(false);
  const [restoreDlg, setRestoreDlg] = useState(null);
  const [restoreMode, setRestoreMode] = useState("missing");
  const [restoreBusy, setRestoreBusy] = useState(false);
  const [uploadBusy, setUploadBusy] = useState(false);
  const backupInput = useRef(null);
  const [qnb, setQnb] = useState(null);
  const [qnbSet, setQnbSet] = useState(null);
  const [qnbTesting, setQnbTesting] = useState(false);
  const [critInfo, setCritInfo] = useState(null);
  const branding = useBranding();
  const logoInput = useRef(null);
  const [logoBusy, setLogoBusy] = useState(false);

  const load = () => { api.get("/users").then((r) => setList(r.data)).catch(() => {}); };
  const loadBackups = () => { api.get("/backups").then((r) => setBackups(r.data)).catch(() => {}); };
  useEffect(() => { load(); loadBackups(); }, []);
  useEffect(() => {
    api.get("/settings/notifications").then((r) => { setNotif(r.data); setExtraStr((r.data.extra_emails || []).join(", ")); }).catch(() => {});
    api.get("/settings/notifications/critical-preview").then((r) => setCritInfo(r.data)).catch(() => {});
  }, []);

  const uploadLogo = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    setLogoBusy(true);
    try {
      const fd = new FormData(); fd.append("file", f);
      await api.post("/settings/branding/logo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await fetchBranding(true);
      toast.success("Şirket logosu güncellendi — yeni PDF'lerde kullanılacak");
    } catch (err) { toast.error(apiError(err)); }
    finally { setLogoBusy(false); }
  };
  const resetLogo = async () => {
    if (!await confirm("Varsayılan GÜMÜŞALAN logosuna dönülsün mü?")) return;
    try { await api.delete("/settings/branding/logo"); await fetchBranding(true); toast.success("Varsayılan logoya dönüldü"); }
    catch (err) { toast.error(apiError(err)); }
  };

  const loadQnb = () => api.get("/qnb/settings").then((r) => {
    setQnb(r.data);
    const s = r.data.settings || {};
    setQnbSet({ mode: s.mode || r.data.mode || "test", series: s.series || r.data.series || "GES", earsiv_series: s.earsiv_series || r.data.earsiv_series || "GEA", irsaliye_series: s.irsaliye_series || r.data.irsaliye_series || "IRS",
      wsdl: s.wsdl || "", efatura_wsdl: s.efatura_wsdl || "", default_profile: s.default_profile || r.data.default_profile || "TICARIFATURA", sube: s.sube || "", kasa: s.kasa || "", tax_office: s.tax_office || "" });
  }).catch(() => {});
  useEffect(() => { loadQnb(); }, []);
  const saveQnb = async () => {
    try {
      if (qnbSet.series && qnbSet.series === qnbSet.earsiv_series) return toast.error("e-Fatura ve e-Arşiv serileri farklı olmalı (GİB kuralı)");
      const { data } = await api.put("/qnb/settings", { mode: qnbSet.mode, series: qnbSet.series, earsiv_series: qnbSet.earsiv_series, irsaliye_series: qnbSet.irsaliye_series, wsdl: qnbSet.wsdl, efatura_wsdl: qnbSet.efatura_wsdl, default_profile: qnbSet.default_profile, sube: qnbSet.sube, kasa: qnbSet.kasa, tax_office: qnbSet.tax_office });
      setQnb((prev) => ({ ...prev, ...data })); toast.success("QNB ayarları kaydedildi");
    } catch (e) { toast.error(apiError(e)); }
  };
  const [qnbSyncing, setQnbSyncing] = useState(false);
  const [qnbSim, setQnbSim] = useState(null);
  const syncQnb = async () => {
    setQnbSyncing(true);
    try {
      const { data } = await api.post("/qnb/incoming/sync");
      const n = (data.incoming_fatura || 0) + (data.incoming_irsaliye || 0);
      (data.errors || []).length ? toast.warning(data.errors.join(" · ")) : toast.success(n ? `${n} yeni belge alındı` : "Yeni belge yok · giden durumlar güncellendi");
      loadQnb();
    } catch (e) { toast.error(apiError(e)); }
    finally { setQnbSyncing(false); }
  };
  const simulateQnb = async () => {
    if (!await confirm("TEST: Karşı firma (erpefaturatest2) bizim VKN'mize örnek e-Fatura gönderecek. Devam?")) return;
    setQnbSim({ loading: true });
    try {
      const { data } = await api.post("/qnb/simulate/incoming", { profile: "TICARIFATURA" });
      setQnbSim(data);
      if (data.mode === "local") toast.warning("QNB test2 alıcıyı tanımıyor → fatura YEREL simülasyon olarak gelen kutusuna işlendi", { duration: 8000 });
      else toast.success(`Karşı taraftan fatura gönderildi (${data.doc_id}) · gelen kutusuna 15-60 sn içinde düşer`);
      loadQnb();
    } catch (e) { setQnbSim(null); toast.error(apiError(e)); }
  };
  const testQnb = async () => {
    setQnbTesting(true);
    try {
      const { data } = await api.post("/qnb/test-connection");
      if (data.ok) toast.success(`QNB bağlantısı başarılı ✓ (${data.mode === "live" ? "CANLI" : "TEST"})`);
      else toast.error(`QNB bağlantı hatası: ${data.error}`, { duration: 6000 });
    } catch (e) { toast.error(apiError(e)); }
    finally { setQnbTesting(false); }
  };

  const toggleRecipient = (id) => setNotif((n) => {
    const s = new Set(n.recipient_user_ids || []);
    s.has(id) ? s.delete(id) : s.add(id);
    return { ...n, recipient_user_ids: [...s] };
  });
  const saveNotif = async () => {
    const emails = extraStr.split(/[,;\n]/).map((s) => s.trim()).filter((s) => s.includes("@"));
    try {
      await api.put("/settings/notifications", {
        enabled: notif.enabled, send_hour: Number(notif.send_hour),
        recipient_user_ids: notif.recipient_user_ids || [], extra_emails: emails,
        critical_enabled: notif.critical_enabled !== false, critical_hour: Number(notif.critical_hour ?? 8),
      });
      setNotif({ ...notif, extra_emails: emails });
      toast.success("Bildirim ayarları kaydedildi");
    } catch (e) { toast.error(apiError(e)); }
  };
  const testNotif = async () => {
    try { await api.post("/settings/notifications/test"); toast.success("Test e-postası kuyruğa alındı"); }
    catch (e) { toast.error(apiError(e)); }
  };
  const testCritical = async () => {
    try { await api.post("/settings/notifications/test-critical"); toast.success("Kritik stok özeti e-postası kuyruğa alındı"); }
    catch (e) { toast.error(apiError(e)); }
  };

  const runBackup = async () => {
    setBackupBusy(true);
    try {
      const { data } = await api.post("/backups/run");
      toast.success(`Yedek alındı: ${data.backup?.total_docs || 0} kayıt`);
      loadBackups();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBackupBusy(false); }
  };
  const downloadBackup = async (b) => {
    // Bearer başlığıyla indir (jeton URL'de taşınmaz)
    try {
      const { data } = await api.get(`/backups/${b.id}/download`, { responseType: "blob", timeout: 120000 });
      const href = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = href; a.download = b.filename;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(href), 5000);
    } catch (e) { toast.error(apiError(e)); }
  };
  const delBackup = async (id) => {
    if (!await confirm("Bu yedek kaydını silmek istediğinize emin misiniz?")) return;
    try { await api.delete(`/backups/${id}`); loadBackups(); toast.success("Yedek silindi"); }
    catch (e) { toast.error(apiError(e)); }
  };
  const [backupCfg, setBackupCfg] = useState(null);
  const [emailBusy, setEmailBusy] = useState(null);
  const [verifyBusy, setVerifyBusy] = useState(null);
  const loadBackupCfg = () => api.get("/backups/config").then((r) => setBackupCfg(r.data)).catch(() => {});
  useEffect(() => { loadBackupCfg(); }, []);
  const verifyBackup = async (b) => {
    setVerifyBusy(b.id);
    try {
      const { data } = await api.post(`/backups/${b.id}/verify`);
      data.ok ? toast.success("Yedek dosyası bütünlük kontrolünden geçti ✓") : toast.error(`Yedek dosyası BOZUK: ${data.backup?.verify_error || "doğrulanamadı"}`);
      loadBackups();
    } catch (e) { toast.error(apiError(e)); }
    finally { setVerifyBusy(null); }
  };
  const emailBackup = async (b) => {
    setEmailBusy(b.id);
    try {
      const { data } = await api.post(`/backups/${b.id}/email`);
      toast.success(`Yedek e-postayla gönderildi → ${(data.sent || []).join(", ")}`);
      loadBackups();
    } catch (e) { toast.error(apiError(e)); }
    finally { setEmailBusy(null); }
  };
  const uploadBackup = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploadBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/backups/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Yedek dosyası yüklendi: ${(data.backup?.total_docs || 0).toLocaleString("tr-TR")} kayıt — şimdi "Geri Yükle" ile uygulayın`);
      loadBackups();
      setRestoreMode("full");
      setRestoreDlg(data.backup);
    } catch (err) { toast.error(apiError(err)); }
    finally { setUploadBusy(false); }
  };
  const runRestore = async () => {
    if (!restoreDlg) return;
    if (restoreMode === "full" && !await confirm("TAM GERİ YÜKLEME: Mevcut TÜM veriler bu yedekteki haliyle değiştirilecek ve yedekten sonra yapılan değişiklikler SİLİNECEK. Bu işlem geri alınamaz. Devam edilsin mi?")) return;
    setRestoreBusy(true);
    try {
      const { data } = await api.post(`/backups/${restoreDlg.id}/restore`, { mode: restoreMode });
      toast.success(`Geri yükleme tamamlandı — ${data.total_restored} kayıt geri yüklendi${restoreMode === "full" ? " · sayfa yenileniyor" : ""}`);
      setRestoreDlg(null);
      if (restoreMode === "full") setTimeout(() => window.location.reload(), 1500);
    } catch (e) { toast.error(apiError(e)); }
    finally { setRestoreBusy(false); }
  };

  const save = async () => {
    if (!form.name.trim()) return toast.error("İsim gerekli");
    try {
      if (editId) {
        const upd = { name: form.name, role: form.role };
        if (form.password) upd.password = form.password;
        if (form.pin) upd.pin = form.pin;
        await api.put(`/users/${editId}`, upd);
      } else {
        if (!form.email.trim() || !form.password) return toast.error("E-posta ve şifre gerekli");
        await api.post("/users", form);
      }
      toast.success("Kullanıcı kaydedildi"); setDlg(false); load();
    } catch (e) { toast.error(apiError(e)); }
  };
  const del = async (u) => { if (await confirm({ title: "Kullanıcı silinsin mi?", description: `${u.name} (${u.email || "e-posta yok"}) sistemden kaldırılacak; bu kullanıcı artık giriş yapamaz.`, destructive: true })) { try { await api.delete(`/users/${u.id}`); toast.success("Kullanıcı silindi"); load(); } catch (e) { toast.error(apiError(e)); } } };

  const SECTIONS = [["users", "Kullanıcılar"], ["security", "Güvenlik"], ["notif", "Bildirimler"], ["branding", "Logo"], ["qnb", "QNB e-Belge"], ["backup", "Yedekler"]];
  const [section, setSection] = useState("users");
  const jump = (k) => { setSection(k); document.getElementById(`sec-${k}`)?.scrollIntoView({ behavior: "smooth", block: "start" }); };
  const initials = (n) => String(n || "?").trim().split(/\s+/).slice(0, 2).map((w) => w[0]).join("").toLocaleUpperCase("tr");
  const admins = list.filter((u) => u.role === "admin").length;
  const lastBackup = backups[0];

  return (
    <div className="space-y-6 stagger-in" data-testid="users-page">
      <PageHeader title="Kullanıcılar & Sistem Ayarları" badge="Yönetim" subtitle="Personel hesapları, e-posta bildirimleri, şirket logosu, QNB e-Belge bağlantısı ve sistem yedekleri tek ekranda">
        <Button onClick={() => { setForm(blank); setEditId(null); setDlg(true); }} data-testid="add-user-btn"><Plus className="h-4 w-4 mr-2" />Yeni Kullanıcı</Button>
      </PageHeader>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="settings-kpis">
        <StatCard label="Kullanıcılar" value={list.length} icon={Users2} tone="primary" sub={`${admins} yönetici · ${list.length - admins} personel`} testid="settings-kpi-users" onClick={() => jump("users")} active={section === "users"} />
        <StatCard label="Sabah Bildirimi" value={notif ? (notif.enabled ? "Açık" : "Kapalı") : "—"} icon={Bell} tone={notif?.enabled ? "success" : "default"} sub={notif ? `${String(notif.send_hour).padStart(2, "0")}:00 · kritik stok ${notif.critical_enabled !== false ? "açık" : "kapalı"}` : "yükleniyor"} testid="settings-kpi-notif" onClick={() => jump("notif")} active={section === "notif"} />
        <StatCard label="QNB e-Belge" value={qnb ? (qnb.configured ? "Hazır" : "Eksik") : "—"} icon={Send} tone={qnb?.configured ? (qnb.mode === "live" ? "success" : "warning") : "danger"} sub={qnb ? `${qnb.mode === "live" ? "CANLI" : "TEST"} ortamı · okunmamış ${qnb.incoming_new ?? 0}` : "yükleniyor"} testid="settings-kpi-qnb" onClick={() => jump("qnb")} active={section === "qnb"} />
        <StatCard label="Son Yedek" value={lastBackup ? fmtDate(lastBackup.created_at) : "Yok"} icon={Database} tone="blue" sub={lastBackup ? `${(lastBackup.total_docs || 0).toLocaleString("tr-TR")} kayıt · ${fmtSize(lastBackup.size)}` : "henüz yedek alınmadı"} testid="settings-kpi-backup" onClick={() => jump("backup")} active={section === "backup"} />
      </div>

      <div className="sticky top-14 lg:top-16 z-20 -mx-4 lg:-mx-8 px-4 lg:px-8 py-2 bg-background/85 backdrop-blur border-b" data-testid="settings-nav">
        <Segmented value={section} onChange={jump} testid="settings-section" size="xs" options={SECTIONS} />
      </div>

      <Panel id="sec-users" title="Kullanıcı Hesapları" subtitle="Yönetici tüm modüllere erişir; depo personeli yalnızca ürün/sayım, talep, hazırlık ve sohbet görür" icon={Users2} testid="users-panel">
        {list.length === 0 ? <Empty /> : (
          <div className="space-y-2.5" data-testid="users-list">
            {list.map((u) => (
              <div key={u.id} className="group relative rounded-xl border bg-card pl-5 pr-4 py-3 overflow-hidden transition-all hover:shadow-md hover:-translate-y-px" data-testid={`user-${u.id}`}>
                <span className={`absolute left-0 top-0 bottom-0 w-1.5 ${u.role === "admin" ? "bg-primary" : "bg-secondary"}`} />
                <div className="flex flex-col lg:flex-row lg:items-center gap-3">
                  <div className={`h-10 w-10 rounded-full flex items-center justify-center font-head font-black text-sm shrink-0 ${u.role === "admin" ? "bg-primary/10 text-primary" : "bg-secondary/10 text-secondary"}`}>{initials(u.name)}</div>
                  <div className="flex-1 grid grid-cols-2 md:grid-cols-4 gap-3 min-w-0">
                    <Cell label="İsim" className="col-span-2 md:col-span-1">
                      <div className="font-head font-black text-[15px] leading-tight truncate">{u.name}</div>
                      <div className="text-[11px] text-muted-foreground truncate">{u.email || "e-posta yok"}</div>
                    </Cell>
                    <Cell label="Rol">
                      <Badge variant={u.role === "admin" ? "default" : "secondary"} className="gap-1">
                        {u.role === "admin" ? <><Shield className="h-3 w-3" />Yönetici</> : <><HardHat className="h-3 w-3" />Depo Müdürü</>}
                      </Badge>
                    </Cell>
                    <Cell label="Giriş PIN'i">
                      <span data-testid={`user-pin-${u.id}`}>{u.has_pin ? <Badge variant="outline" className="text-emerald-700 border-emerald-300 bg-emerald-50 gap-1"><KeyRound className="h-3 w-3" />Tanımlı</Badge> : <span className="text-xs text-muted-foreground">Tanımlı değil</span>}</span>
                    </Cell>
                    <Cell label="Oluşturma"><div className="text-sm tabular-nums font-semibold inline-flex items-center gap-1"><CalendarDays className="h-3.5 w-3.5 text-muted-foreground" />{fmtDate(u.created_at)}</div></Cell>
                  </div>
                  <div className="flex items-center gap-1 lg:justify-end shrink-0 lg:pl-3 lg:border-l">
                    <Button size="sm" variant="outline" className="h-8" onClick={() => { setForm({ name: u.name, email: u.email, password: "", role: u.role, pin: "" }); setEditId(u.id); setDlg(true); }} data-testid={`user-edit-${u.id}`}><Pencil className="h-3.5 w-3.5 mr-1" />Düzenle</Button>
                    <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => del(u)} title="Sil" data-testid={`user-del-${u.id}`}><Trash2 className="h-4 w-4 text-destructive" /></Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </Panel>

      <Panel id="sec-security" title="Güvenlik · Giriş Parolası & Engeller" subtitle="Site giriş parolası, güvenilir ağlar ve engellenen bağlantılar · ayrıntılı giriş kayıtları: Aktiviteler › Girişler" icon={ShieldCheck} testid="security-panel">
        <SecurityPanel />
      </Panel>

      {notif && (
        <Panel id="sec-notif" title="Sabah Vade Özeti Bildirimi" subtitle="Vadesi geçmiş / yaklaşan fatura ve çek özeti seçili kişilere e-posta ile gönderilir" icon={Bell} testid="notif-panel"
          actions={<div className="flex items-center gap-2"><Switch checked={!!notif.enabled} onCheckedChange={(v) => setNotif({ ...notif, enabled: v })} data-testid="notif-enabled" /><Label className="text-sm">{notif.enabled ? "Açık" : "Kapalı"}</Label></div>}>
          <div className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="space-y-1.5">
              <Label className="text-xs font-bold uppercase tracking-wider text-muted-foreground flex items-center gap-1"><Clock className="h-3 w-3" />Gönderim Saati</Label>
              <Select value={String(notif.send_hour)} onValueChange={(v) => setNotif({ ...notif, send_hour: Number(v) })}>
                <SelectTrigger data-testid="notif-hour"><SelectValue /></SelectTrigger>
                <SelectContent className="max-h-64">
                  {Array.from({ length: 24 }, (_, h) => <SelectItem key={h} value={String(h)}>{String(h).padStart(2, "0")}:00</SelectItem>)}
                </SelectContent>
              </Select>
              <p className="text-[10px] text-muted-foreground">Europe/İstanbul saati</p>
            </div>
            <div className="sm:col-span-2 space-y-1.5">
              <Label className="text-xs font-bold uppercase tracking-wider text-muted-foreground flex items-center gap-1"><Mail className="h-3 w-3" />Ek E-postalar (virgülle ayırın)</Label>
              <Input value={extraStr} onChange={(e) => setExtraStr(e.target.value)} placeholder="ornek@mail.com, ikinci@mail.com" data-testid="notif-extra-emails" />
            </div>
          </div>

          <div className="space-y-2">
            <Label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Alıcı Kullanıcılar</Label>
            <p className="text-[11px] text-muted-foreground">Hiç seçim yapılmazsa tüm yöneticilere gönderilir.</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {list.map((u) => {
                const noEmail = !u.email;
                const checked = (notif.recipient_user_ids || []).includes(u.id);
                return (
                  <label key={u.id} className={`flex items-center gap-2 border rounded-lg px-3 py-2 ${noEmail ? "opacity-50" : "cursor-pointer hover:bg-muted/40"}`} data-testid={`notif-user-${u.id}`}>
                    <Checkbox checked={checked} disabled={noEmail} onCheckedChange={() => toggleRecipient(u.id)} />
                    <div className="min-w-0">
                      <div className="text-sm font-semibold truncate">{u.name}</div>
                      <div className="text-[11px] text-muted-foreground truncate">{u.email || "e-posta yok"}</div>
                    </div>
                    <Badge variant={u.role === "admin" ? "default" : "secondary"} className="ml-auto text-[10px]">{u.role === "admin" ? "Yönetici" : "Depo Müdürü"}</Badge>
                  </label>
                );
              })}
            </div>
          </div>

          <div className="rounded-xl border bg-muted/20 p-4 space-y-3" data-testid="notif-critical-block">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div className="flex items-center gap-2">
                <div className="h-8 w-8 rounded-lg bg-red-500/10 text-red-600 flex items-center justify-center"><PackageSearch className="h-4 w-4" /></div>
                <div>
                  <div className="font-bold text-sm leading-tight">Günlük Kritik Stok Özeti</div>
                  <div className="text-[11px] text-muted-foreground">Kritik seviyenin altına düşen ürünler her gün tek e-postayla aynı alıcılara gönderilir. Kritik ürün yoksa e-posta atılmaz.</div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Switch checked={notif.critical_enabled !== false} onCheckedChange={(v) => setNotif({ ...notif, critical_enabled: v })} data-testid="notif-critical-enabled" />
                <Label className="text-sm">{notif.critical_enabled !== false ? "Açık" : "Kapalı"}</Label>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 items-end">
              <div className="space-y-1.5">
                <Label className="text-xs font-bold uppercase tracking-wider text-muted-foreground flex items-center gap-1"><Clock className="h-3 w-3" />Gönderim Saati</Label>
                <Select value={String(notif.critical_hour ?? 8)} onValueChange={(v) => setNotif({ ...notif, critical_hour: Number(v) })}>
                  <SelectTrigger data-testid="notif-critical-hour"><SelectValue /></SelectTrigger>
                  <SelectContent className="max-h-64">
                    {Array.from({ length: 24 }, (_, h) => <SelectItem key={h} value={String(h)}>{String(h).padStart(2, "0")}:00</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="sm:col-span-2 flex flex-wrap items-center justify-between gap-2">
                {critInfo ? (
                  <div className="flex flex-wrap gap-1.5 text-[11px]" data-testid="notif-critical-stats">
                    <Badge variant="outline" className="border-red-300 bg-red-50 text-red-700">Kritik ürün: {critInfo.total}</Badge>
                    <Badge variant="outline" className="border-orange-300 bg-orange-50 text-orange-700">Stoğu sıfır: {critInfo.zero}</Badge>
                    {(critInfo.brands || []).slice(0, 3).map((b) => <Badge key={b.brand} variant="secondary" className="text-[10px]">{b.brand} · {b.count}</Badge>)}
                  </div>
                ) : <span />}
                <Button variant="outline" size="sm" onClick={testCritical} data-testid="notif-critical-test"><Send className="h-4 w-4 mr-1.5" />Kritik Stok Test E-postası</Button>
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={testNotif} data-testid="notif-test"><Send className="h-4 w-4 mr-1.5" />Test E-postası Gönder</Button>
            <Button onClick={saveNotif} data-testid="notif-save">Kaydet</Button>
          </div>
          </div>
        </Panel>
      )}

      <Panel id="sec-branding" title="Şirket Logosu (PDF & Belgeler)" subtitle="Teklif, satış fişi, cari defter, tahsilat makbuzu ve ekstre PDF'lerinde kullanılan logo · şeffaf PNG önerilir" icon={ImageIcon} testid="branding-panel"
        actions={branding?.logo_url
          ? <Badge className="bg-emerald-600" data-testid="branding-status">Özel logo · {branding.logo_width}×{branding.logo_height} px</Badge>
          : <Badge variant="secondary" data-testid="branding-status">Varsayılan logo</Badge>}>
        <div className="flex flex-col sm:flex-row gap-4 items-start">
          <div className="rounded-xl border bg-white p-4 flex items-center justify-center w-full sm:w-72 h-28 shrink-0">
            <img src={branding?.logo_url ? fileUrl(branding.logo_url) : COMPANY.logo} alt="Şirket logosu" className="max-h-20 max-w-full object-contain" data-testid="branding-logo-preview" />
          </div>
          <div className="space-y-2 flex-1">
            <input type="file" accept="image/png,image/jpeg,image/webp" ref={logoInput} className="hidden" onChange={uploadLogo} data-testid="branding-logo-input" />
            <div className="flex gap-2 flex-wrap">
              <Button onClick={() => logoInput.current?.click()} disabled={logoBusy} data-testid="branding-logo-upload">{logoBusy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Upload className="h-4 w-4 mr-1.5" />}Yeni Logo Yükle</Button>
              {branding?.logo_url && <Button variant="outline" onClick={resetLogo} data-testid="branding-logo-reset"><RotateCcw className="h-4 w-4 mr-1.5" />Varsayılana Dön</Button>}
            </div>
            <p className="text-[11px] text-muted-foreground">Yüklenen görsel otomatik PNG'ye çevrilir (en fazla 1200 px genişlik, 5 MB). Değişiklik yeni oluşturulan tüm PDF ve önizlemelere anında yansır.</p>
            {branding?.updated_at && <p className="text-[11px] text-muted-foreground">Son güncelleme: {fmtDateTime(branding.updated_at)}{branding.updated_by ? ` · ${branding.updated_by}` : ""}</p>}
          </div>
        </div>
      </Panel>

      {qnb && (
        <Panel id="sec-qnb" title="QNB e-Fatura / e-Arşiv / e-İrsaliye" subtitle="QNB eSolutions özel entegratör bağlantı ayarları, servis durumu ve senkron bilgisi" icon={Send} testid="qnb-panel"
          actions={qnb.configured
            ? <Badge className="bg-emerald-600"><CheckCircle2 className="h-3 w-3 mr-1" />Bağlantı Hazır · {qnb.mode === "live" ? "CANLI" : "TEST"}</Badge>
            : <Badge variant="destructive"><AlertTriangle className="h-3 w-3 mr-1" />Yapılandırma Eksik</Badge>}>
          <div className="space-y-4">
          {qnb.password_age?.warn && (
            <div className={`rounded-lg border p-3 text-sm flex gap-2 ${qnb.password_age.expired ? "border-red-300 bg-red-50 text-red-800" : "border-amber-300 bg-amber-50 text-amber-900"}`} data-testid="qnb-password-warning">
              <KeyRound className="h-4 w-4 shrink-0 mt-0.5" />
              <div><b>QNB parola hatırlatıcısı:</b> {qnb.password_age.expired ? `web servis parolası ${qnb.password_age.days} gündür değiştirilmedi — 90 günlük süre doldu.` : `90 günlük parola süresi ${qnb.password_age.remaining} gün içinde doluyor.`} QNB portalında parolayı yenileyip aşağıdaki <b>Canlı Kimlikler</b> alanından güncelleyin.</div>
            </div>
          )}
          {qnb.mode === "live" && (
            <div className="rounded-lg border border-red-300 bg-red-50 p-3 text-xs text-red-800" data-testid="qnb-live-summary-bar">
              <b>CANLI ORTAM</b> · seriler sabit: e-Fatura <b>{qnb.series}</b> · e-Arşiv <b>{qnb.earsiv_series}</b> · e-İrsaliye <b>{qnb.irsaliye_series}</b> · şablonlar: <b>{qnb.templates?.efatura || "—"}</b> / <b>{qnb.templates?.earsiv || "—"}</b>
            </div>
          )}
          {!qnb.configured && (
            <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-800" data-testid="qnb-missing">
              Eksik alanlar: <b>{(qnb.missing || []).map((m) => QNB_LABELS[m] || m).join(", ")}</b>. Bu değerler sunucu ortam değişkenleri (secrets) olarak eklenmelidir.
            </div>
          )}

          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-xs">
            {Object.entries(qnb.fields || {}).map(([k, v]) => (
              <div key={k} className={`flex items-center gap-1.5 rounded border px-2 py-1 ${v ? "border-emerald-200 bg-emerald-50" : "border-red-200 bg-red-50"}`} data-testid={`qnb-field-${k}`}>
                {v ? <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600 shrink-0" /> : <AlertTriangle className="h-3.5 w-3.5 text-red-500 shrink-0" />}
                <span className="truncate">{QNB_LABELS[k] || k}</span>
              </div>
            ))}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-xs" data-testid="qnb-info">
            <div className="rounded-lg border p-3 space-y-1">
              <div className="font-black uppercase tracking-wider text-secondary">Firma (Gönderici)</div>
              <div><b>{qnb.company?.company_title}</b> · {qnb.company?.owner_name}</div>
              <div className="font-mono">VKN/TCKN: {qnb.company?.vkn}</div>
              {qnb.company?.efatura_vkn && qnb.company.efatura_vkn !== qnb.company.vkn && (
                <div className="font-mono text-amber-700" data-testid="qnb-efatura-vkn">e-Fatura test kimliği: {qnb.company.efatura_vkn}</div>
              )}
              <div>{qnb.company?.district} / {qnb.company?.city} · {qnb.company?.email}</div>
              <div data-testid="qnb-company-tax-office">Vergi Dairesi: <b>{qnb.company?.tax_office || "—"}</b></div>
            </div>
            <div className="rounded-lg border p-3 space-y-1">
              <div className="font-black uppercase tracking-wider text-secondary">Servisler</div>
              <div className="flex items-center gap-1">{qnb.efatura_ready ? <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" /> : <AlertTriangle className="h-3.5 w-3.5 text-red-500" />}e-Fatura / e-İrsaliye connector <span className="text-muted-foreground truncate">({(qnb.efatura_wsdl || "").replace(/^https?:\/\//, "").split("/")[0]})</span></div>
              <div className="flex items-center gap-1">{qnb.earsiv_ready ? <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" /> : <AlertTriangle className="h-3.5 w-3.5 text-red-500" />}e-Arşiv <span className="text-muted-foreground truncate">({(qnb.earsiv_wsdl || "").replace(/^https?:\/\//, "").split("/")[0]})</span></div>
              <div className="flex items-center gap-1">{qnb.simulator ? <CheckCircle2 className="h-3.5 w-3.5 text-amber-600" /> : <span className="h-3.5 w-3.5 rounded-full border inline-block" />}Test karşı taraf simülatörü {qnb.company?.sim_vkn ? <span className="font-mono text-muted-foreground">(VKN {qnb.company.sim_vkn})</span> : "(yok)"}</div>
            </div>
            <div className="rounded-lg border p-3 space-y-1">
              <div className="font-black uppercase tracking-wider text-secondary">Senkron</div>
              <div>Son çalışma: <b>{qnb.sync?.last_run ? fmtDateTime(qnb.sync.last_run) : "—"}</b></div>
              <div>Gelen e-Fatura son sıra: {qnb.sync?.last_sira_FATURA ?? "—"} · e-İrsaliye: {qnb.sync?.last_sira_IRSALIYE ?? "—"}</div>
              <div>Okunmamış gelen belge: <b>{qnb.incoming_new ?? 0}</b> · Arka plan: her 60 sn + saatlik cron</div>
              {(qnb.sync?.last_result?.errors || []).length > 0 && <div className="text-red-600">{qnb.sync.last_result.errors.join(" · ")}</div>}
            </div>
          </div>

          {qnbSet && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Ortam</Label>
                <Select value={qnbSet.mode} onValueChange={(v) => setQnbSet({ ...qnbSet, mode: v })}>
                  <SelectTrigger data-testid="qnb-mode"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="test">TEST (qnbesolutions test ortamı)</SelectItem>
                    <SelectItem value="live" disabled={!qnb.live_check?.valid && qnb.mode !== "live"} data-testid="qnb-mode-live">CANLI (qnbesolutions.com.tr){!qnb.live_check?.valid && qnb.mode !== "live" ? " — önce Canlıya Geçiş Kontrolleri ✓" : ""}</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-[10px] text-muted-foreground">{qnb.mode === "live" ? "Sistem CANLI: kesilen belgeler gerçek GİB kaydıdır." : "Sistem TEST: kesilen belgeler gerçek değildir. CANLI seçimi, aşağıdaki hazırlık kontrolleri geçmeden açılmaz."}</p>
              </div>
              <div className="grid grid-cols-3 gap-2">
                <div className="space-y-1.5">
                  <Label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">e-Fatura Serisi</Label>
                  <Input value={qnb.live_fixed ? qnb.series : qnbSet.series} disabled={qnb.live_fixed} onChange={(e) => setQnbSet({ ...qnbSet, series: e.target.value.toUpperCase().slice(0, 3) })} data-testid="qnb-series" placeholder="GES" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">e-Arşiv Serisi</Label>
                  <Input value={qnb.live_fixed ? qnb.earsiv_series : qnbSet.earsiv_series} disabled={qnb.live_fixed} onChange={(e) => setQnbSet({ ...qnbSet, earsiv_series: e.target.value.toUpperCase().slice(0, 3) })} data-testid="qnb-earsiv-series" placeholder="GEA" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">İrsaliye Serisi</Label>
                  <Input value={qnb.live_fixed ? qnb.irsaliye_series : qnbSet.irsaliye_series} disabled={qnb.live_fixed} onChange={(e) => setQnbSet({ ...qnbSet, irsaliye_series: e.target.value.toUpperCase().slice(0, 3) })} data-testid="qnb-irsaliye-series" placeholder="IRS" />
                </div>
                {qnb.live_fixed && <p className="col-span-3 text-[10px] text-red-700" data-testid="qnb-series-fixed-note">Canlı ortamda seriler sabittir (GEA / GAA / GI) — değiştirilemez.</p>}
              </div>
              <div className="space-y-1.5 sm:col-span-2">
                <Label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">e-Arşiv WSDL Adresi</Label>
                <Input value={qnbSet.wsdl} onChange={(e) => setQnbSet({ ...qnbSet, wsdl: e.target.value })} data-testid="qnb-wsdl" placeholder={qnb?.earsiv_wsdl || "https://.../EarsivWebService?wsdl"} />
                <p className="text-[10px] text-muted-foreground">Boşsa sunucu ayarı / ortam bazlı varsayılan kullanılır. Canlıya geçişte QNB'nin verdiği canlı adres buraya yapıştırılır.</p>
              </div>
              <div className="space-y-1.5 sm:col-span-2">
                <Label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">e-Fatura / e-İrsaliye Connector WSDL</Label>
                <Input value={qnbSet.efatura_wsdl} onChange={(e) => setQnbSet({ ...qnbSet, efatura_wsdl: e.target.value })} data-testid="qnb-efatura-wsdl" placeholder={qnb?.efatura_wsdl || "https://.../connectorService?wsdl"} />
                <p className="text-[10px] text-muted-foreground">Boşsa e-Fatura mükellefine fatura kesilmez (yanlış tür engellenir); gelen e-Fatura da alınamaz.</p>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Varsayılan e-Fatura Senaryosu</Label>
                <Select value={qnbSet.default_profile} onValueChange={(v) => setQnbSet({ ...qnbSet, default_profile: v })}>
                  <SelectTrigger data-testid="qnb-default-profile"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="TICARIFATURA">Ticari Fatura (alıcı KABUL/RED yanıtı verir)</SelectItem>
                    <SelectItem value="TEMELFATURA">Temel Fatura (yanıt yok)</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-[10px] text-muted-foreground">Fatura kesme ekranında her seferinde değiştirilebilir.</p>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div className="space-y-1.5"><Label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Şube</Label><Input value={qnbSet.sube} onChange={(e) => setQnbSet({ ...qnbSet, sube: e.target.value })} data-testid="qnb-sube" placeholder="DFLT" /></div>
                <div className="space-y-1.5"><Label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Kasa</Label><Input value={qnbSet.kasa} onChange={(e) => setQnbSet({ ...qnbSet, kasa: e.target.value })} data-testid="qnb-kasa" placeholder="DFLT" /></div>
              </div>
              <div className="space-y-1.5 sm:col-span-2">
                <Label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Firma Vergi Dairesi</Label>
                <TaxOfficeSelect value={qnbSet.tax_office ?? ""} onChange={(v) => setQnbSet({ ...qnbSet, tax_office: v })} data-testid="qnb-tax-office" placeholder={qnb.company?.tax_office || "Büyükçekmece Vergi Dairesi"} />
                <p className="text-[10px] text-muted-foreground">Kesilen e-Fatura / e-Arşiv / e-İrsaliye belgelerinde satıcı bilgisinde yazılır. Şu an kullanılan: <b data-testid="qnb-tax-office-current">{qnb.company?.tax_office || "—"}</b></p>
              </div>
            </div>
          )}

          {qnbSim && !qnbSim.loading && (
            <div className={`rounded-lg border p-3 text-xs space-y-1 ${qnbSim.mode === "local" ? "border-amber-300 bg-amber-50 text-amber-900" : "border-emerald-300 bg-emerald-50 text-emerald-900"}`} data-testid="qnb-sim-result">
              <div className="font-bold">Test gönderimi: {qnbSim.doc_id} · {qnbSim.sender_title} → bize · QNB durumu: {qnbSim.status_label}</div>
              {qnbSim.hint && <div>{qnbSim.hint}</div>}
              <div>Gelen kutusu: <b>Alış Faturaları → Gelen e-Faturalar</b> sekmesi.</div>
            </div>
          )}

          <div className="flex justify-end gap-2 flex-wrap">
            {qnb.simulator && <Button variant="outline" className="border-amber-300 text-amber-700 hover:bg-amber-50" onClick={simulateQnb} disabled={!!qnbSim?.loading} data-testid="qnb-simulate">{qnbSim?.loading ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : null}Test: Karşı taraftan fatura gönder</Button>}
            <Button variant="outline" onClick={syncQnb} disabled={qnbSyncing} data-testid="qnb-sync-now">{qnbSyncing ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-1.5" />}Şimdi Senkronize Et</Button>
            <Button variant="outline" onClick={testQnb} disabled={qnbTesting} data-testid="qnb-test">{qnbTesting ? "Test ediliyor..." : "Bağlantıyı Test Et"}</Button>
            <Button onClick={saveQnb} data-testid="qnb-save">Ayarları Kaydet</Button>
          </div>

          <QnbLiveCheck qnb={qnb} onChanged={loadQnb} />
          </div>
        </Panel>
      )}

      <Panel id="sec-backup" title="Sistem Yedekleri" subtitle="Tüm verilerin (ürün, teklif, fatura, cari, hareket) zaman damgalı JSON arşivi · her gece 22:00'de otomatik alınır ve e-postayla gönderilir" icon={Database} testid="backup-panel"
        actions={<>
          <input ref={backupInput} type="file" accept=".gz,.json,application/gzip" className="hidden" onChange={uploadBackup} data-testid="backup-upload-input" />
          <Button variant="outline" size="sm" className="h-8" onClick={loadBackups} data-testid="backup-refresh"><RefreshCw className="h-4 w-4 mr-1.5" />Yenile</Button>
          <Button variant="outline" size="sm" className="h-8" onClick={() => backupInput.current?.click()} disabled={uploadBusy} data-testid="backup-upload">
            {uploadBusy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Upload className="h-4 w-4 mr-1.5" />}Yedek Dosyası Yükle
          </Button>
          <Button size="sm" className="h-8" onClick={runBackup} disabled={backupBusy} data-testid="backup-run">
            {backupBusy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Database className="h-4 w-4 mr-1.5" />}Şimdi Yedek Al
          </Button>
        </>}>
        <div className="mb-3 space-y-3">
          <div className="rounded-xl border border-emerald-200 bg-emerald-50/50 p-3 text-xs leading-relaxed flex flex-wrap items-center gap-x-4 gap-y-1" data-testid="backup-email-info">
            <span className="inline-flex items-center gap-1.5 font-bold text-emerald-800"><Mail className="h-4 w-4" />Gece Yedeği E-postası · her gün 22:00</span>
            <span className="text-emerald-900/80">Alıcılar: {(backupCfg?.recipients || []).length ? (backupCfg.recipients || []).map((r) => <b key={r} className="font-mono mr-2" data-testid="backup-recipient">{r}</b>) : <i>yapılandırılmamış</i>}</span>
            <span className="text-muted-foreground">Bağlantı bir indirme sayfası açar (7 gün geçerli); otomatik yedekler {backupCfg?.keep_days || 30} gün saklanır.</span>
            {backupCfg?.link_base && <span className="text-muted-foreground w-full">E-postadaki bağlantı adresi: <b className="font-mono" data-testid="backup-link-base">{backupCfg.link_base}</b>{backupCfg.env === "preview" && backupCfg.emails_from_this_env === false ? " · e-postalar YAYIN ortamından gönderilir" : ""}</span>}
          </div>
          {backupCfg && (
            <div className={`rounded-xl border p-3 text-xs flex flex-wrap items-center gap-x-4 gap-y-1 ${backupCfg.night_ok ? "border-emerald-200 bg-emerald-50/40" : "border-amber-300 bg-amber-50"}`} data-testid="backup-night-status">
              <span className={`inline-flex items-center gap-1.5 font-bold ${backupCfg.night_ok ? "text-emerald-800" : "text-amber-900"}`}>
                {backupCfg.night_ok ? <CheckCircle2 className="h-4 w-4" /> : <AlertTriangle className="h-4 w-4" />}
                {backupCfg.night_ok ? "Gece yedeği alındı" : "Son 22:00 yedeği henüz yok"}
              </span>
              <span className="text-muted-foreground">Son otomatik yedek: <b className="text-foreground">{backupCfg.last_auto ? fmtDateTime(backupCfg.last_auto.created_at) : "—"}</b>{backupCfg.last_auto?.verified ? " · doğrulandı ✓" : ""}</span>
              <span className="text-muted-foreground">Bekçi: {backupCfg.watchdog}</span>
              {backupCfg.last_failure && <span className="text-red-700 w-full" data-testid="backup-last-failure">Son başarısız deneme: {fmtDateTime(backupCfg.last_failure.created_at)} · {backupCfg.last_failure.error}</span>}
            </div>
          )}
          <PublishSyncCard />
          <details className="rounded-xl border bg-muted/20 p-3 text-xs leading-relaxed" data-testid="backup-migrate-guide">
            <summary className="font-bold text-sm cursor-pointer flex items-center gap-2"><Upload className="h-4 w-4 text-primary" />Elle taşıma (yedek dosyasıyla) — alternatif yol</summary>
            <ol className="list-decimal pl-5 mt-2 space-y-0.5 text-muted-foreground">
              <li>Verilerin bulunduğu ortamda (önizleme) <b>"Şimdi Yedek Al"</b> → en güncel yedeğin yanındaki <b>"İndir"</b> ile <span className="font-mono">yedek_….json.gz</span> dosyasını kaydedin.</li>
              <li>Yayındaki sitede bu sayfayı açıp <b>"Yedek Dosyası Yükle"</b> ile aynı dosyayı seçin.</li>
              <li>Açılan pencerede <b>"Tam Geri Yükle"</b> → onaylayın. Tüm ürünler, cariler, faturalar, teklifler ve ayarlar birebir aktarılır; sayfa yenilenir (gerekirse yeniden giriş yapın).</li>
            </ol>
          </details>
        </div>
        {backups.length === 0 ? (
          <div className="text-sm text-muted-foreground py-6 text-center border border-dashed rounded-xl bg-muted/20" data-testid="backup-empty">Henüz yedek alınmadı. "Şimdi Yedek Al" ile ilk yedeği oluşturun.</div>
        ) : (
          <div className="space-y-2" data-testid="backup-list">
            {backups.map((b, i) => (
              <div key={b.id} className={`group relative rounded-xl border bg-card pl-5 pr-4 py-3 overflow-hidden transition-all hover:shadow-md hover:-translate-y-px ${b.failed ? "border-red-200 bg-red-50/40" : ""}`} data-testid={`backup-${b.id}`}>
                <span className={`absolute left-0 top-0 bottom-0 w-1.5 ${b.failed ? "bg-red-500" : i === 0 ? "bg-[#16A34A]" : String(b.created_by).startsWith("manuel") ? "bg-primary" : "bg-secondary"}`} />
                <div className="flex flex-col lg:flex-row lg:items-center gap-3">
                  <div className="flex-1 grid grid-cols-2 md:grid-cols-4 gap-3 min-w-0">
                    <Cell label="Tarih" className="col-span-2 md:col-span-1">
                      <div className="font-head font-bold text-sm tabular-nums">{fmtDateTime(b.created_at)}</div>
                      {b.failed ? <span className="text-[10px] font-bold text-red-700" data-testid={`backup-failed-${b.id}`}>BAŞARISIZ · {b.error || "hata"}</span> : i === 0 && <span className="text-[10px] font-bold text-emerald-700">En güncel yedek</span>}
                    </Cell>
                    <Cell label="Kayıt"><div className="font-mono text-sm font-bold tabular-nums">{b.failed ? "—" : (b.total_docs || 0).toLocaleString("tr-TR")}</div></Cell>
                    <Cell label="Boyut · Bütünlük">
                      <div className="font-mono text-sm tabular-nums text-muted-foreground">{b.failed ? "—" : fmtSize(b.size)}</div>
                      {!b.failed && (b.verified === true ? <span className="text-[10px] font-bold text-emerald-700 inline-flex items-center gap-1" title={b.sha256 ? `SHA-256 ${b.sha256.slice(0, 16)}…` : ""} data-testid={`backup-verified-${b.id}`}><ShieldCheck className="h-3 w-3" />Doğrulandı</span>
                        : b.verified === false ? <span className="text-[10px] font-bold text-red-700 inline-flex items-center gap-1" title={b.verify_error} data-testid={`backup-corrupt-${b.id}`}><AlertTriangle className="h-3 w-3" />Bozuk · {b.verify_error}</span>
                        : <span className="text-[10px] text-muted-foreground" data-testid={`backup-unverified-${b.id}`}>Doğrulanmadı</span>)}
                    </Cell>
                    <Cell label="Kaynak"><Badge variant={String(b.created_by).startsWith("manuel") ? "default" : "secondary"} className="text-[10px]">{b.created_by}</Badge>
                      {b.emailed_to?.length ? <div className="text-[10px] text-emerald-700 mt-0.5 inline-flex items-center gap-1" data-testid={`backup-emailed-${b.id}`}><Mail className="h-3 w-3" />E-posta gönderildi ({b.emailed_to.length})</div> : b.email_error ? <div className="text-[10px] text-destructive mt-0.5" title={b.email_error} data-testid={`backup-email-error-${b.id}`}>E-posta hatası</div> : null}
                    </Cell>
                  </div>
                  <div className="flex items-center gap-1 lg:justify-end shrink-0 lg:pl-3 lg:border-l">
                    {!b.failed && <>
                      <Button size="sm" variant="outline" className="h-8" onClick={() => verifyBackup(b)} disabled={verifyBusy === b.id} title="Dosyayı depodan okuyup bütünlüğünü doğrula" data-testid={`backup-verify-${b.id}`}>{verifyBusy === b.id ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <ShieldCheck className="h-3.5 w-3.5 mr-1 text-emerald-600" />}Doğrula</Button>
                      <Button size="sm" variant="outline" className="h-8" onClick={() => emailBackup(b)} disabled={emailBusy === b.id} title="Yedek bağlantısını yapılandırılmış adreslere e-postayla gönder" data-testid={`backup-email-${b.id}`}>{emailBusy === b.id ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Mail className="h-3.5 w-3.5 mr-1 text-emerald-600" />}E-posta</Button>
                      <Button size="sm" variant="outline" className="h-8" onClick={() => downloadBackup(b)} data-testid={`backup-download-${b.id}`}><Download className="h-3.5 w-3.5 mr-1 text-primary" />İndir</Button>
                      <Button size="sm" variant="outline" className="h-8" onClick={() => { setRestoreMode("missing"); setRestoreDlg(b); }} data-testid={`backup-restore-${b.id}`}><RotateCcw className="h-3.5 w-3.5 mr-1 text-secondary" />Geri Yükle</Button>
                    </>}
                    <Button size="icon" variant="ghost" className="h-8 w-8" title="Sil" onClick={() => delBackup(b.id)} data-testid={`backup-del-${b.id}`}><Trash2 className="h-4 w-4 text-destructive" /></Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </Panel>

      <Dialog open={!!restoreDlg} onOpenChange={(o) => !o && setRestoreDlg(null)}>
        <DialogContent className="max-w-lg" data-testid="restore-dialog">
          <DialogHeader><DialogTitle className="font-head text-2xl flex items-center gap-2"><RotateCcw className="h-6 w-6 text-primary" />Yedekten Geri Yükle</DialogTitle></DialogHeader>
          {restoreDlg && (
            <div className="space-y-3">
              <p className="text-sm text-muted-foreground">{fmtDateTime(restoreDlg.created_at)} · {(restoreDlg.total_docs || 0).toLocaleString("tr-TR")} kayıt{restoreDlg.source_filename ? ` · yüklenen dosya: ${restoreDlg.source_filename}` : ""}</p>
              {String(restoreDlg.created_by || "").startsWith("yükleme") && (
                <div className="rounded-lg border border-amber-300 bg-amber-50 text-amber-900 text-xs p-2.5" data-testid="restore-upload-hint">
                  Başka ortamdan yüklenen yedek. Verileri buraya <b>birebir taşımak</b> için <b>"Tam Geri Yükle"</b> seçin. İşlem sonrası sayfa yenilenir; oturum düşerse aynı kullanıcı/şifre ile yeniden giriş yapın.
                </div>
              )}
              <button onClick={() => setRestoreMode("missing")} className={`w-full text-left rounded-xl border p-3 transition-colors ${restoreMode === "missing" ? "border-primary bg-primary/5 ring-1 ring-primary" : "hover:bg-muted/50"}`} data-testid="restore-mode-missing">
                <div className="font-bold text-sm flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-[#16A34A]" />Eksik Kayıtları Geri Yükle (Güvenli)</div>
                <div className="text-xs text-muted-foreground mt-1">Yalnızca silinmiş/eksik kayıtları geri ekler. Mevcut verilerinizi değiştirmez veya silmez. Yanlışlıkla silinen ürün, teklif, fatura ve carileri kurtarmak için idealdir.</div>
              </button>
              <button onClick={() => setRestoreMode("full")} className={`w-full text-left rounded-xl border p-3 transition-colors ${restoreMode === "full" ? "border-destructive bg-destructive/5 ring-1 ring-destructive" : "hover:bg-muted/50"}`} data-testid="restore-mode-full">
                <div className="font-bold text-sm flex items-center gap-2"><AlertTriangle className="h-4 w-4 text-destructive" />Tam Geri Yükle (Tehlikeli)</div>
                <div className="text-xs text-muted-foreground mt-1">Tüm verileri bu yedekteki haline döndürür — yedekten sonra yapılan tüm değişiklikler SİLİNİR. Geri alınamaz.</div>
              </button>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setRestoreDlg(null)}>İptal</Button>
            <Button onClick={runRestore} disabled={restoreBusy} data-testid="restore-confirm">{restoreBusy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RotateCcw className="h-4 w-4 mr-2" />}Geri Yükle</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={dlg} onOpenChange={setDlg}>
        <DialogContent className="max-w-md" data-testid="user-dialog">
          <DialogHeader><DialogTitle className="font-head text-2xl">{editId ? "Kullanıcı Düzenle" : "Yeni Kullanıcı"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <F label="İsim *"><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="u-name" /></F>
            <F label="E-posta *"><Input type="email" value={form.email} disabled={!!editId} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="u-email" /></F>
            <F label={editId ? "Yeni Şifre (boş bırakılabilir)" : "Şifre *"}><Input type="text" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="u-password" /></F>
            <F label={editId ? "Giriş PIN'i (değiştirmek için 4-6 hane)" : "Giriş PIN'i (4-6 hane, opsiyonel)"}>
              <Input inputMode="numeric" maxLength={6} value={form.pin} onChange={(e) => setForm({ ...form, pin: e.target.value.replace(/\D/g, "").slice(0, 6) })} placeholder="Örn. 1453" data-testid="u-pin" />
            </F>
            <F label="Rol">
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger data-testid="u-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="worker">Depo Müdürü</SelectItem>
                  <SelectItem value="admin">Yönetici</SelectItem>
                </SelectContent>
              </Select>
            </F>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDlg(false)}>İptal</Button>
            <Button onClick={save} data-testid="save-user">Kaydet</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const F = ({ label, children }) => (
  <div className="space-y-1.5">
    <Label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">{label}</Label>
    {children}
  </div>
);
